import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Typography, 
  message, 
  Tag, 
  Progress, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Space,
  Tooltip,
  Checkbox,
  Switch
} from 'antd';
import { ReloadOutlined, KeyOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { handleFetchResponse } from '../utils/authUtils';
import { API_ENDPOINTS } from '../utils/apiConfig';
import EditUserModal from './EditUserModal';

const { Title } = Typography;

interface DataTableProps {
  dataType: 'A' | 'B' | 'C';
  title: string;
}

interface TableData {
  id: number;
  [key: string]: any;
}

interface ApiResponse {
  data: TableData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const DataTable: React.FC<DataTableProps> = ({ dataType, title }) => {
  const { user } = useAuth();
  const [data, setData] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TableData | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordForm] = Form.useForm();
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [userToEdit, setUserToEdit] = useState<TableData | null>(null);
  const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<TableData | null>(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [deleteUserForm] = Form.useForm();
  const [toggleMultipleTasksLoading, setToggleMultipleTasksLoading] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Check if current user has permission to reset passwords
  const canResetPassword = user?.role === 'Admin' || user?.role === 'Leader';
  
  // Check if current user has permission to edit users (only for Type A data)
  const canEditUsers = (user?.role === 'Admin' || user?.role === 'Leader') && dataType === 'A';

  /**
   * Check if current user can manage a specific user
   * @param targetUserRole - Role of the user to be managed
   * @returns boolean indicating if the operation is allowed
   */
  const canManageUser = (targetUserRole: string): boolean => {
    if (user?.role === 'Admin') return true;
    if (user?.role === 'Leader') return targetUserRole === 'Employee';
    return false;
  };

  /**
   * Fetch data from the API
   * @param page - Current page number
   * @param pageSize - Number of items per page
   */
  const fetchData = async (page: number = 1, pageSize: number = 10) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: pageSize,
      };

      const endpoint = API_ENDPOINTS[`DATA_${dataType}`];
      
      const response = await axios.get<ApiResponse>(endpoint, {
        params,
      });

      setData(response.data.data);
      setPagination({
        current: response.data.page,
        pageSize: response.data.limit,
        total: response.data.total,
      });
    } catch (error: any) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        message.error('You do not have permission to access this data');
      } else {
        message.error('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, [dataType]);

  /**
   * Handle table pagination change
   * @param page - New page number
   * @param pageSize - New page size
   */
  const handleTableChange = (page: number, pageSize: number) => {
    fetchData(page, pageSize);
  };

  /**
   * Open reset password modal for a specific user
   * @param record - User record from table
   */
  const handleResetPassword = (record: TableData) => {
    if (!canResetPassword) {
      message.error('You do not have permission to reset passwords');
      return;
    }

    // Check if current user can manage this specific user
    if (!canManageUser(record.role)) {
      message.error('You do not have permission to reset password for this user');
      return;
    }
    
    setSelectedUser(record);
    setResetPasswordModalVisible(true);
    resetPasswordForm.resetFields();
  };

  /**
   * Handle reset password form submission
   * @param values - Form values containing new password
   */
  const handleResetPasswordSubmit = async (values: { newPassword: string }) => {
    if (!selectedUser) return;

    setResetPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: selectedUser.username,
          newPassword: values.newPassword,
        }),
      });

      await handleFetchResponse(
        response,
        (data) => {
          message.success('Password reset successfully');
          setResetPasswordModalVisible(false);
          setSelectedUser(null);
          resetPasswordForm.resetFields();
        },
        (data) => {
          message.error(data.message || 'Failed to reset password');
        }
      );
    } catch (error) {
      console.error('Error resetting password:', error);
      message.error('An error occurred while resetting the password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  /**
   * Close reset password modal
   */
  const handleResetPasswordCancel = () => {
    setResetPasswordModalVisible(false);
    setSelectedUser(null);
    resetPasswordForm.resetFields();
  };

  /**
   * Open edit user modal for a specific user
   * @param record - User record from table
   */
  const handleEditUser = (record: TableData) => {
    if (!canEditUsers) {
      message.error('You do not have permission to edit users');
      return;
    }

    // Check if current user can manage this specific user
    if (!canManageUser(record.role)) {
      message.error('You do not have permission to edit this user');
      return;
    }
    
    setUserToEdit(record);
    setEditUserModalVisible(true);
  };

  /**
   * Handle edit user success
   */
  const handleEditUserSuccess = () => {
    // Refresh the data to show updated information
    fetchData(pagination.current, pagination.pageSize);
  };

  /**
   * Close edit user modal
   */
  const handleEditUserCancel = () => {
    setEditUserModalVisible(false);
    setUserToEdit(null);
  };

  /**
   * Handle delete user button click
   * @param record - User record from table
   */
  const handleDeleteUser = (record: TableData) => {
    setUserToDelete(record);
    setDeleteUserModalVisible(true);
    deleteUserForm.resetFields();
  };

  /**
   * Handle delete user form submission
   * @param values - Form values containing deletion options
   */
  const handleDeleteUserSubmit = async (values: { force?: boolean; reason?: string }) => {
    if (!userToDelete) return;

    setDeleteUserLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.AUTH_DELETE_USER}/${userToDelete.username}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          force: values.force || false,
          reason: values.reason || 'No reason provided'
        }),
      });

      await handleFetchResponse(
        response,
        (data) => {
          message.success('User deleted successfully');
          setDeleteUserModalVisible(false);
          setUserToDelete(null);
          deleteUserForm.resetFields();
          // Refresh the data
          fetchData(pagination.current, pagination.pageSize);
        },
        (data) => {
          message.error(data.message || 'Failed to delete user');
        }
      );
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('An error occurred while deleting the user');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  /**
   * Handle delete user modal cancel
   */
  const handleDeleteUserCancel = () => {
    setDeleteUserModalVisible(false);
    setUserToDelete(null);
    deleteUserForm.resetFields();
  };

  /**
   * Handle toggle multiple tasks setting for a user
   * @param record - User record from table
   */
  const handleToggleMultipleTasks = async (record: TableData, checked: boolean) => {
    if (!canResetPassword) {
      message.error('You do not have permission to toggle this setting');
      return;
    }

    // Check if current user can manage this specific user
    if (!canManageUser(record.role)) {
      message.error('You do not have permission to toggle this setting for this user');
      return;
    }

    // Only allow for Employee role
    if (record.role !== 'Employee') {
      message.warning('This setting is only available for Employee users');
      return;
    }

    // Save current state for potential revert
    const previousData = [...data];

    // Optimistically update UI
    const updatedData = data.map((item) => 
      item.username === record.username 
        ? { ...item, allowMultipleTasks: checked }
        : item
    );
    setData(updatedData);

    setToggleMultipleTasksLoading(record.username);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TOGGLE_MULTIPLE_TASKS(record.username), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      await handleFetchResponse(
        response,
        (responseData) => {
          const newValue = responseData.allowMultipleTasks ?? checked;
          message.success(
            `Allow multiple tasks ${newValue ? 'enabled' : 'disabled'} for ${record.username}`
          );
          // Refresh the data to ensure consistency with backend
          fetchData(pagination.current, pagination.pageSize);
        },
        (errorData) => {
          // Revert optimistic update on error
          setData(previousData);
          message.error(errorData.message || 'Failed to toggle multiple tasks setting');
        }
      );
    } catch (error) {
      // Revert optimistic update on error
      setData(previousData);
      console.error('Error toggling multiple tasks:', error);
      message.error('An error occurred while toggling multiple tasks setting');
    } finally {
      setToggleMultipleTasksLoading(null);
    }
  };

  /**
   * Get column definitions based on data type
   */
  const getColumns = () => {
    const baseColumns = (() => {
      switch (dataType) {
        case 'A':
          return [
            {
              title: 'Username',
              dataIndex: 'username',
              key: 'username',
            },
            {
              title: 'Team',
              dataIndex: 'team',
              key: 'team',
            },
            {
              title: 'Role',
              dataIndex: 'role',
              key: 'role',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (status: string) => (
                <Tag color={status === 'Active' ? 'green' : 'red'}>
                  {status}
                </Tag>
              ),
            },
            {
              title: 'Multiple Tasks',
              dataIndex: 'allowMultipleTasks',
              key: 'allowMultipleTasks',
              align: 'center' as const,
              render: (allowMultipleTasks: boolean, record: TableData) => {
                // Only show for Employee role
                if (record.role !== 'Employee') {
                  return <span style={{ color: '#aaa' }}>-</span>;
                }
                
                // Check if current user can manage this user
                const canManageThisUser = canManageUser(record.role);
                
                if (canManageThisUser && canResetPassword) {
                  // Show Switch for Admin/Leader (similar to Priority toggle)
                  return (
                    <Switch
                      checked={!!allowMultipleTasks}
                      checkedChildren={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                      unCheckedChildren={<CloseCircleOutlined style={{ color: '#d9d9d9' }} />}
                      onChange={(checked) => handleToggleMultipleTasks(record, checked)}
                      loading={toggleMultipleTasksLoading === record.username}
                    />
                  );
                } else {
                  // Show Tag for read-only view
                  return (
                    <Tag color={allowMultipleTasks ? 'green' : 'default'}>
                      {allowMultipleTasks ? 'Allowed' : 'Not Allowed'}
                    </Tag>
                  );
                }
              },
            },
            {
              title: 'Last Login',
              dataIndex: 'lastLogin',
              key: 'lastLogin',
              render: (date: string) => new Date(date).toLocaleString(),
            },
          ];

        case 'B':
          return [
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              width: 80,
            },
            {
              title: 'Project Name',
              dataIndex: 'projectName',
              key: 'projectName',
              sorter: true,
            },
            {
              title: 'Leader',
              dataIndex: 'leader',
              key: 'leader',
            },
            {
              title: 'Team Size',
              dataIndex: 'teamSize',
              key: 'teamSize',
              sorter: true,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (status: string) => {
                const colorMap: { [key: string]: string } = {
                  'In Progress': 'blue',
                  'Planning': 'orange',
                  'Completed': 'green',
                  'On Hold': 'red',
                };
                return (
                  <Tag color={colorMap[status] || 'default'}>
                    {status}
                  </Tag>
                );
              },
            },
            {
              title: 'Progress',
              dataIndex: 'progress',
              key: 'progress',
              render: (progress: number) => (
                <Progress percent={progress} size="small" />
              ),
            },
            {
              title: 'Budget',
              dataIndex: 'budget',
              key: 'budget',
              render: (budget: number) => budget ? `$${budget.toLocaleString()}` : '-',
              sorter: true,
            },
            {
              title: 'Start Date',
              dataIndex: 'startDate',
              key: 'startDate',
            },
            {
              title: 'End Date',
              dataIndex: 'endDate',
              key: 'endDate',
            },
          ];

        case 'C':
          return [
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              width: 80,
            },
            {
              title: 'Employee Name',
              dataIndex: 'employeeName',
              key: 'employeeName',
              sorter: true,
            },
            {
              title: 'Position',
              dataIndex: 'position',
              key: 'position',
            },
            {
              title: 'Department',
              dataIndex: 'department',
              key: 'department',
              filters: [
                { text: 'Engineering', value: 'Engineering' },
                { text: 'Design', value: 'Design' },
                { text: 'Product', value: 'Product' },
                { text: 'Marketing', value: 'Marketing' },
                { text: 'Business', value: 'Business' },
                { text: 'Analytics', value: 'Analytics' },
                { text: 'Sales', value: 'Sales' },
              ],
            },
            {
              title: 'Hire Date',
              dataIndex: 'hireDate',
              key: 'hireDate',
            },
            {
              title: 'Salary',
              dataIndex: 'salary',
              key: 'salary',
              render: (salary: number) => salary ? `$${salary.toLocaleString()}` : '-',
              sorter: true,
            },
            {
              title: 'Performance',
              dataIndex: 'performance',
              key: 'performance',
              render: (performance: string) => {
                const colorMap: { [key: string]: string } = {
                  'Excellent': 'green',
                  'Good': 'blue',
                  'Average': 'orange',
                  'Poor': 'red',
                };
                return (
                  <Tag color={colorMap[performance] || 'default'}>
                    {performance}
                  </Tag>
                );
              },
            },
            {
              title: 'Attendance (%)',
              dataIndex: 'attendance',
              key: 'attendance',
              render: (attendance: number) => (
                <Progress percent={attendance} size="small" />
              ),
              sorter: true,
            },
          ];

        default:
          return [];
      }
    })();

    // Add action column if user has permission
    if (canResetPassword || canEditUsers) {
      baseColumns.push({
        title: 'Actions',
        key: 'actions',
        width: 120,
        fixed: 'right' as const,
        render: (_: any, record: TableData): React.ReactNode => {
          // Check if current user can manage this specific user
          const canManageThisUser = canManageUser(record.role);
          
          return (
            <Space>
              {canEditUsers && canManageThisUser && (
                <Tooltip title="Edit User">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => handleEditUser(record)}
                    style={{ color: '#52c41a' }}
                  />
                </Tooltip>
              )}
              {canResetPassword && canManageThisUser && (
                <Tooltip title="Reset Password">
                  <Button
                    type="text"
                    icon={<KeyOutlined />}
                    size="small"
                    onClick={() => handleResetPassword(record)}
                    style={{ color: '#1890ff' }}
                  />
                </Tooltip>
              )}
              {canEditUsers && canManageThisUser && record.role !== 'Admin' && (
                <Tooltip title="Delete User">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleDeleteUser(record)}
                    style={{ color: '#ff4d4f' }}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      } as any);
    }

    return baseColumns;
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: isMobile ? '12px' : '16px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <Title level={isMobile ? 4 : 3} style={{ margin: 0, fontSize: isMobile ? '18px' : '20px' }}>
          {title}
        </Title>
        <ReloadOutlined 
          onClick={() => fetchData(pagination.current, pagination.pageSize)}
          style={{ 
            fontSize: isMobile ? '16px' : '18px', 
            cursor: 'pointer', 
            color: '#1890ff',
            flexShrink: 0
          }}
        />
      </div>

      <Table
        columns={getColumns()}
        dataSource={data}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: !isMobile, // Hide on mobile
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`,
          size: isMobile ? 'small' : 'default',
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        loading={loading}
        onChange={(pagination) => 
          handleTableChange(pagination.current || 1, pagination.pageSize || 10)
        }
        scroll={{ 
          x: isMobile ? 'max-content' : 1200,
          y: isMobile ? 'calc(100vh - 300px)' : undefined
        }}
        size={isMobile ? 'small' : 'middle'}
      />

      {/* Reset Password Modal */}
      <Modal
        title={
          <Space>
            <KeyOutlined />
            Reset Password
          </Space>
        }
        open={resetPasswordModalVisible}
        onCancel={handleResetPasswordCancel}
        footer={null}
        destroyOnClose
      >
        <Form
          form={resetPasswordForm}
          layout="vertical"
          onFinish={handleResetPasswordSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="User"
            style={{ marginBottom: '16px' }}
          >
            <Input
              value={selectedUser?.username || ''}
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleResetPasswordCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={resetPasswordLoading}
              >
                Reset Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            Delete User
          </Space>
        }
        open={deleteUserModalVisible}
        onCancel={handleDeleteUserCancel}
        footer={null}
        destroyOnClose
      >
        <Form
          form={deleteUserForm}
          layout="vertical"
          onFinish={handleDeleteUserSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="User"
            style={{ marginBottom: '16px' }}
          >
            <Input
              value={userToDelete?.username || ''}
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason for Deletion"
            rules={[
              { required: true, message: 'Please provide a reason for deletion' }
            ]}
          >
            <Input.TextArea 
              placeholder="Enter reason for deletion (e.g., User left the company)"
              rows={3}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleDeleteUserCancel}>
                Cancel
              </Button>
              <Button 
                danger
                htmlType="submit" 
                loading={deleteUserLoading}
                icon={<DeleteOutlined />}
              >
                Delete User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <EditUserModal
        visible={editUserModalVisible}
        user={userToEdit}
        onCancel={handleEditUserCancel}
        onSuccess={handleEditUserSuccess}
        userRole={user?.role || ''}
      />
    </div>
  );
};

export default DataTable; 